import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { io } from "socket.io-client";
import useAuthStore from "stores/useAuthStore";
import { ApiSetting } from "services/ApiSettings";

const SocketContext = createContext(null);

export function useSocket() {
  return useContext(SocketContext);
}

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);

  const createSocket = useCallback(() => {
    const userId = useAuthStore.getState().userAuth?.id;
    if (!userId) return;

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const newSocket = io(ApiSetting.DEFAULT_HOST, {
      withCredentials: true,
      query: { user_id: userId },
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  }, []);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
    }
  }, []);

  useEffect(() => {
    createSocket();

    const handleTokenRefresh = () => {
      console.debug("Token refreshed, reconnecting socket");
      createSocket();
    };

    const handleAuthFailure = () => {
      console.debug("Auth failed, disconnecting socket");
      disconnectSocket();
    };

    window.addEventListener("tokenRefreshed", handleTokenRefresh);
    window.addEventListener("authFailed", handleAuthFailure);

    return () => {
      window.removeEventListener("tokenRefreshed", handleTokenRefresh);
      window.removeEventListener("authFailed", handleAuthFailure);
      disconnectSocket();
    };
  }, [disconnectSocket, createSocket]);

  return (
    <SocketContext.Provider value={{ socket, createSocket, disconnectSocket }}>
      {children}
    </SocketContext.Provider>
  );
}
